import { startAuthentication } from '@simplewebauthn/browser';
import { useState } from 'react';

import { api } from '~/client/lib/trpc';
import { useAppStore } from '~/client/store/app/appStore';
import { useTinybaseStore } from '~/client/store/tinybase/tinybaseStore';

import { useCurrentUser } from '../storeHooks';
import { useDeviceId } from '../useDeviceId';
import { getCredentialsString, useClientAuthInfo } from './useClientAuthInfo';

type Params = {
  onAuthFailure: () => void;
};

export const useHandleAuth = ({ onAuthFailure }: Params) => {
  const deviceId = useDeviceId();
  const {
    authOpts: storedAuthOpts,
    storeAuthOpts,
    username: localStorageUsername,
    hashAndStoreAuthResp,
    verifyAuthResp,
    hasAuthResp,
  } = useClientAuthInfo();

  const currentUser = useCurrentUser();
  const setUser = useAppStore((state) => state.setUser);
  const setNeedsClientAuth = useAppStore((state) => state.setNeedsClientAuth);
  const userStoreCrypto = useTinybaseStore((state) => state.userStoreCrypto);
  const createUserStoreCrypto = useTinybaseStore((state) => state.createUserStoreCrypto);

  const [authenticating, setAuthenticating] = useState(false);

  const { mutateAsync: generateAuth } = api.generateAuth.useMutation();
  const { mutateAsync: verifyAuth } = api.verifyAuth.useMutation();

  const username = currentUser?.email || localStorageUsername;

  const handleAuth = async () => {
    setAuthenticating(true);
    try {
      if (!username) {
        throw Error('No username found');
      }
      const authOpts = await generateAuth({ username, deviceId });
      const authResp = await startAuthentication({ optionsJSON: authOpts });
      const user = await verifyAuth({ username, deviceId, params: authResp });

      storeAuthOpts(authOpts);
      await hashAndStoreAuthResp(authResp.response);
      await createUserStoreCrypto(user.id, getCredentialsString(authResp.response));

      setUser(user);
      setNeedsClientAuth(false);
    } catch (err) {
      console.error((err as Error).toString());
      onAuthFailure();
    }
    setAuthenticating(false);
  };

  const handleClientAuth = async () => {
    setAuthenticating(true);
    try {
      if (!storedAuthOpts || !hasAuthResp || !currentUser) {
        throw Error('Cannot perform client auth');
      }
      const authResp = await startAuthentication({ optionsJSON: storedAuthOpts });
      await verifyAuthResp(authResp.response);
      const credentialsString = getCredentialsString(authResp.response);

      if (userStoreCrypto) {
        await userStoreCrypto.generateKey(credentialsString);
      } else {
        await createUserStoreCrypto(currentUser.id, credentialsString);
      }
      setNeedsClientAuth(false);
    } catch (err) {
      console.error((err as Error).toString());
      onAuthFailure();
    }
    setAuthenticating(false);
  };

  return { handleAuth, handleClientAuth, authenticating, username };
};
